import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormFields } from "libs/fields";
import { onError } from "libs/error";
import { Auth } from "aws-amplify";
import { SubmitButton, TextInput, ClickButton } from "components/FormElements";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";
import Modal from "components/Modal";
import { LocationParams } from "libs/Common";

export default function VerifyEmail() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation() as LocationParams;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element>();
  const [showModal, setShowModal] = useState<boolean>(false);

  const [fields, handleFieldChange] = useFormFields({
    code: ""
  });

  function validateForm() {
    return fields.code.length === 6;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);

    try {
      await Auth.verifyCurrentUserAttributeSubmit("email", fields.code);
      setIsLoading(false);
      setShowModal(true);
    } catch (error: unknown) {
      const e = error as Error;
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setIsLoading(false);
    }
  }

  async function resendConfirmationCode(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    setIsLoading(true);

    try {
      await Auth.resendSignUp(location.state.username);
      setIsLoading(false);
    } catch (error: unknown) {
      const e = error as Error;
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setIsLoading(false);
    }
  }

  async function handleLogout() {
    setIsLoading(true);
    await Auth.signOut({ global: true });
    setIsLoading(false);

    navigate("/");
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <SubHeader
        heading={t("local.account.changepass.title.verifyemail").toString()}
        subheading={t("local.account.subtitle.subheader").toString()}
        path="/account"
      />
      <main className="flex-grow bg-ct-lightgrey px-4 md:px-0">
        <h2 className="text-3xl text-black text-center pt-20 mb-2.5">{t("local.account.changepass.title.verifyemail")}</h2>
        <p className="text-base text-black text-center mb-6">{t("local.account.subtitle.entercode")}</p>
        <form className="container mx-auto max-w-md flex flex-col justify-center space-y-4 pb-20" onSubmit={handleSubmit}>
          <TextInput handleChange={handleFieldChange} name="code" value={fields.code} label={t("global.label.confirmationcode")} />
          <SubmitButton disabled={!validateForm() || isLoading} loading={isLoading} label={t("global.button.completeverification")} />
          <p className="boutton bg-gray-100 inline-flex text-sm border border-ct-grey text-gray-800 px-4 py-3">
            <i className="icon-drawer"></i>
            <span className="ml-2">{t("global.label.checkemailforcode")}</span>
          </p>
          <ClickButton handleClick={resendConfirmationCode} disabled={isLoading} loading={isLoading} label={t("global.button.sendnewcode")} />
          {errorMessage}
        </form>
      </main>
      <Footer />
      {showModal && (
        <Modal
          title={t("local.account.changeemail.label.emailsuccess").toString()}
          subtitle={t("local.account.changeemail.title.success").toString()}
          handleClick={() => handleLogout()}
          clickLabel={t("global.button.continue")}
        />
      )}
    </div>
  );
}
